"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { atom, useAtom } from "jotai";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/animate-ui/radix/checkbox";
import { toast } from "@/components/pop-ui/pop-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { regionDefaultLanguage } from "@/lib/i18n/settings";
import { buildLocaleString } from "@/lib/i18n/utils";
import { privacyConsentAtom } from "@/lib/privacy/atom";
import { useLocaleRouter } from "@/lib/utils.client";
import { RuleModal } from "@/modules/cms/rule-modal";
import { useORPC } from "@/providers/orpc-provider";
import { Button as FormButton, Input } from "../components/form-items";
import { LoginSocialApple, LoginSocialGoogle } from "../components/login-social";
import { formatPhoneParam, handleAuthApiError, isEmail, type UserFormErrors, validateEmail, validatePhoneNumber } from "../utils";
interface DefaultFormProps {
  area: string;
  setArea: (value: string) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  onUserExists: (data?: any) => void;
  onUserNotExists: (data?: any) => void;
}

// 内部勾选状态 atom
const agreementCheckedAtom = atom(false);
export function CheckForm({
  area,
  setArea,
  inputValue,
  setInputValue,
  onUserExists,
  onUserNotExists
}: DefaultFormProps) {
  const [errors, setErrors] = useState<UserFormErrors>({
    account: "",
    password: ""
  });
  const {
    t
  } = useTranslation();
  const orpc = useORPC();
  const router = useLocaleRouter();
  const [privacyConsent] = useAtom(privacyConsentAtom);
  const [isAgreementChecked, setIsAgreementChecked] = useAtom(agreementCheckedAtom);

  // 静态配置的国家列表 - 只支持港澳台英四个地区
  const areaList = [{
    code: "GB",
    key: "area.gb",
    phoneCode: "+44",
    digitLength: [10, 11, 12]
  }
  // {
  // 	code: "HK",
  // 	key: "area.hk",
  // 	phoneCode: "+852",
  // 	digitLength: [8],
  // },
  // {
  // 	code: "MO",
  // 	key: "area.mo",
  // 	phoneCode: "+853",
  // 	digitLength: [8, 9, 10, 11, 12, 13, 14, 15],
  // },
  // {
  // 	code: "TW",
  // 	key: "area.tw",
  // 	phoneCode: "+886",
  // 	digitLength: [9],
  // },
  ];
  const checkUserExistsMutation = useMutation(orpc.user.public_userCheckUserExists.mutationOptions());
  useEffect(() => {
    setIsAgreementChecked(privacyConsent || false);
  }, [privacyConsent, setIsAgreementChecked]);

  // 处理用户输入变化
  const handleInputChange = useCallback((value: string) => {
    // 清除错误
    setErrors(prev => ({
      ...prev,
      account: ""
    }));
    setInputValue(value);
  }, [setInputValue]);

  // 使用 ref 来跟踪是否正在格式化，避免重复格式化
  const isFormattingRef = useRef(false);

  // 处理输入框失焦时的格式化
  const handleInputBlur = useCallback((value_0: string) => {
    if (isFormattingRef.current) return;
    formatInput(value_0);
  }, []);

  // 统一的输入格式化函数
  const formatInput = useCallback((value_1: string) => {
    // 移除空格，适用于邮箱和手机号
    const cleanedInput = value_1.replace(/\s/g, "");
    if (cleanedInput !== value_1) {
      isFormattingRef.current = true;
      setInputValue(cleanedInput);
      // 重置格式化标志
      requestAnimationFrame(() => {
        isFormattingRef.current = false;
      });
    }
  }, [setInputValue]);

  // 处理国家/地区变更
  const handleAreaChange = (value_2: string) => {
    setArea(value_2);
    // 如果当前输入不是邮箱，清除错误（因为地区变更可能影响手机号验证）
    if (!isEmail(inputValue)) {
      setErrors(prev_0 => ({
        ...prev_0,
        account: ""
      }));
    }

    // 获取新地区的默认语言
    const areaLower = value_2.toLowerCase();
    const newAreaDefaultLanguage = regionDefaultLanguage[areaLower as keyof typeof regionDefaultLanguage];

    // 构建新的locale，使用新地区的默认语言
    const newLocale = buildLocaleString(newAreaDefaultLanguage, areaLower);

    // 获取当前URL路径
    const pathname = window.location.pathname;
    const pathParts = pathname.split("/");

    // 构建新的URL路径
    const newPathParts = [...pathParts];
    if (pathParts[1] && pathParts[1].length > 0) {
      newPathParts[1] = newLocale;
    } else {
      newPathParts.splice(1, 0, newLocale);
    }
    const newPath = newPathParts.join("/");

    // 切换路由
    router.replace(newPath as any);
  };

  // 处理表单提交
  const handleUserCheck = async (e: FormEvent) => {
    e.preventDefault();

    // 清除之前的错误
    setErrors({
      account: "",
      password: ""
    });

    // 检查是否同意条款
    if (!isAgreementChecked) {
      toast.error(t("error.mustAgreeToTerms"));
      return;
    }

    // 验证输入
    if (!inputValue.trim()) {
      setErrors(prev_1 => ({
        ...prev_1,
        account: t("error.account.required")
      }));
      return;
    }

    // 根据输入类型进行验证
    if (isEmail(inputValue)) {
      if (!validateEmail(inputValue)) {
        setErrors(prev_2 => ({
          ...prev_2,
          account: t("error.email.invalid")
        }));
        return;
      }
    } else {
      // 手机号验证
      const areaInfo = areaList.find(item => item.code === area);
      if (!areaInfo) {
        setErrors(prev_3 => ({
          ...prev_3,
          account: t("error.area.invalid")
        }));
        return;
      }

      // 清理输入，移除空格和其他非数字字符
      const cleanedInput_0 = inputValue.replace(/\D/g, "");

      // 对于英国号码，特殊处理本地号码格式
      if (area === "GB") {
        // 检查是否为有效的英国号码格式
        // 支持两种格式：
        // 1. 本地号码: 7xxx xxxxxx (10 digits starting with 7) 或 1xxx xxxxxx (10 digits starting with 1-9)
        // 2. 带国家代码: 44xxxxxxxxxx (12 digits starting with 44)
        if (/^[1-9]\d{9}$/.test(cleanedInput_0) || /^44\d{10}$/.test(cleanedInput_0)) {
          // 号码格式正确，继续处理
        } else {
          setErrors(prev_4 => ({
            ...prev_4,
            account: t("error.phone.invalid")
          }));
          return;
        }
      } else {
        // 使用 libphonenumber-js 进行验证
        const result = validatePhoneNumber(cleanedInput_0, area);
        if (!result.valid) {
          setErrors(prev_5 => ({
            ...prev_5,
            account: t("error.phone.invalid")
          }));
          return;
        }

        // 检查区域代码是否匹配
        if (result.regionCode !== areaInfo.phoneCode) {
          setErrors(prev_6 => ({
            ...prev_6,
            account: t("error.phone.invalid")
          }));
          return;
        }
      }
    }
    try {
      let payload: {
        email?: string;
        phone?: string;
      } = {};
      if (isEmail(inputValue)) {
        payload = {
          email: inputValue
        };
      } else {
        // 手机号验证
        const areaInfo_0 = areaList.find(item_0 => item_0.code === area);
        if (areaInfo_0) {
          const phoneValue = formatPhoneParam(inputValue, area);
          payload = {
            phone: phoneValue
          };
        }
      }
      const result_0 = await checkUserExistsMutation.mutateAsync(payload);
      if (result_0.exists) {
        onUserExists(result_0);
      } else {
        onUserNotExists(result_0);
      }
    } catch (error) {
      await handleAuthApiError(error, "error.checkUserFailed");
    }
  };
  const isLoading = checkUserExistsMutation.isPending;
  return <form onSubmit={handleUserCheck} className="flex flex-col gap-3">
			<div className=" ">
				<Select value={area} onValueChange={handleAreaChange}>
					<SelectTrigger className="w-full">
						<SelectValue placeholder={t("placeholder.selectCountryOrRegion")} />
					</SelectTrigger>
					<SelectContent side="top">
						{areaList.map(item_1 => <SelectItem key={item_1.code} value={item_1.code}>
								{t(item_1.key)}
							</SelectItem>)}
					</SelectContent>
				</Select>
			</div>

			<Input id="inputValue" type="text" placeholder={t("placeholder.enterEmailOrPhone")} value={inputValue} onChange={e_0 => handleInputChange(e_0.target.value)} onBlur={e_1 => handleInputBlur(e_1.target.value)} disabled={isLoading} className="text-xs! md:text-base!" />
			{errors.account && <p className="text-destructive text-sm leading-[1.5]">
					{errors.account}
				</p>}

			<FormButton type="submit" className="w-full" size="lg" loading={isLoading}>
				{isLoading ? t("button.validating") : t("button.continue")}
			</FormButton>
		</form>;
}
export function CheckFormExtra(t0) {
  const $ = _c(65);
  const {
    area,
    onSocialLoginSuccess
  } = t0;
  const {
    t
  } = useTranslation();
  const [isAgreementChecked, setIsAgreementChecked] = useAtom(agreementCheckedAtom);
  let t1;
  if ($[0] !== isAgreementChecked || $[1] !== t) {
    t1 = () => {
      if (!isAgreementChecked) {
        toast.error(t("error.mustAgreeToTerms"));
        return;
      }
    };
    $[0] = isAgreementChecked;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleSocialLogin = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="h-px w-4 bg-black" />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("text.joinWith");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <span className="px-3 text-xs leading-none text-black md:text-sm">{t3}</span>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="h-px w-4 bg-black" />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4) {
    t6 = <div className="mx-auto mt-6 flex items-center justify-start md:mt-8 md:justify-center">{t2}{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = !isAgreementChecked;
  let t8;
  if ($[11] !== area || $[12] !== handleSocialLogin || $[13] !== onSocialLoginSuccess || $[14] !== t7) {
    t8 = <LoginSocialGoogle area={area} disabled={t7} onDisabledClick={handleSocialLogin} onSuccess={onSocialLoginSuccess} />;
    $[11] = area;
    $[12] = handleSocialLogin;
    $[13] = onSocialLoginSuccess;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = !isAgreementChecked;
  let t10;
  if ($[16] !== area || $[17] !== handleSocialLogin || $[18] !== onSocialLoginSuccess || $[19] !== t9) {
    t10 = <LoginSocialApple area={area} disabled={t9} onDisabledClick={handleSocialLogin} onSuccess={onSocialLoginSuccess} />;
    $[16] = area;
    $[17] = handleSocialLogin;
    $[18] = onSocialLoginSuccess;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t8) {
    t11 = <div className="mt-3 flex justify-center gap-2">{t8}{t10}</div>;
    $[21] = t10;
    $[22] = t8;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== setIsAgreementChecked) {
    t12 = checked => setIsAgreementChecked(checked === "indeterminate" ? false : checked);
    $[24] = setIsAgreementChecked;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== isAgreementChecked || $[27] !== t12) {
    t13 = <Checkbox id="agreement-checkbox" className="mt-px size-3.5 md:mt-0 md:size-4 border border-gray-300 data-[state=unchecked]:bg-white data-[state=checked]:bg-black data-[state=checked]:border-black data-[state=checked]:text-white" checked={isAgreementChecked} onCheckedChange={t12} />;
    $[26] = isAgreementChecked;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t) {
    t14 = t("text.agreementPrefix");
    $[29] = t;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14) {
    t15 = <label htmlFor="agreement-checkbox">{t14}</label>;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t) {
    t16 = t("text.termsAndConditions");
    $[33] = t;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t) {
    t17 = t("text.termsAndConditions");
    $[35] = t;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t17) {
    t18 = <span className="text-primary cursor-pointer underline underline-offset-4">{t17}</span>;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t16 || $[40] !== t18) {
    t19 = <RuleModal slug="terms-and-conditions" title={t16}>{t18}</RuleModal>;
    $[39] = t16;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t) {
    t20 = t("text.and");
    $[42] = t;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t) {
    t21 = t("text.privacyPolicy");
    $[44] = t;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t) {
    t22 = t("text.privacyPolicy");
    $[46] = t;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t22) {
    t23 = <span className="text-primary cursor-pointer underline underline-offset-4">{t22}</span>;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t21 || $[51] !== t23) {
    t24 = <RuleModal slug="privacy-policy" title={t21}>{t23}</RuleModal>;
    $[50] = t21;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t15 || $[54] !== t19 || $[55] !== t20 || $[56] !== t24) {
    t25 = <span className="cursor-pointer text-left md:text-center">{t15}{" "}{t19}{" "}{t20}{" "}{t24}</span>;
    $[53] = t15;
    $[54] = t19;
    $[55] = t20;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t13 || $[59] !== t25) {
    t26 = <div className="text-muted-foreground mt-3 w-full text-center text-xs md:mt-6"><div className="mb-2 flex items-start justify-center gap-2">{t13}{t25}</div></div>;
    $[58] = t13;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t11 || $[62] !== t26 || $[63] !== t6) {
    t27 = <>{t6}{t11}{t26}</>;
    $[61] = t11;
    $[62] = t26;
    $[63] = t6;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  return t27;
}